import { useState, useEffect, useRef } from "react";
import { Button } from "devextreme-react";
import { postValidateFilter, postMasterGridFilter, postMasterGridColumnFilter } from "../../actions/gridFilter/gridFilterActions";
import { Stepper } from 'react-form-stepper';
import { FilterTypeSelection } from "./FilterTypeSelection";
import { FilterValidationGrid } from "./FilterValidationGrid";
import { CopyPasteGrid } from "./CopyPasteGrid";
import { ColumnsFilter } from "./ColumnsFilter";
import { ColumnsFilterReview } from "./ColumnsFilterReview"

import './WizardStepper.css';


export const WizardStepper = (props) => {
    const { onApplyFilter } = props;
    const [data, setData] = useState([])
    const [dataValidated, setDataValidated] = useState(null)
    const [step, setStep] = useState(0)
    const [typeOfSearchId, setTypeOfSearchId] = useState(1)
    const [shouldShowError, setShouldShowError] = useState(true);
    const [filterName, setFilterName] = useState(null);
    const [selectedSearch, setSelectedSearch] = useState(null);
    const [filterRows, setFilterRows] = useState([{
        id: 0,
        column: null,
        columnType: null,
        operator: null,
        operatorName: null,
        filterValue: null,
        filterValue2: null,
        logicalOperator: null,
    }]);

    // Clear data when search type changes (not navigation)
    const prevTypeOfSearchId = useRef(typeOfSearchId);
    useEffect(() => {
        // Only clear if typeOfSearchId actually changed (not on first mount)
        if (prevTypeOfSearchId.current !== typeOfSearchId) {
            console.log(`TypeOfSearchId changed from ${prevTypeOfSearchId.current} to ${typeOfSearchId}, clearing data`);
            setData([]);
            setDataValidated(null);
            setFilterName(null);
            setSelectedSearch(null);
        }
        prevTypeOfSearchId.current = typeOfSearchId;
    }, [typeOfSearchId]);

    let typeOfSearchColumnName = null;
    let typeOfSearchColumn = null;

    if (typeOfSearchId === 1) {
        typeOfSearchColumnName = 'Part Name';
        typeOfSearchColumn = 'partName';
    }
    else if (typeOfSearchId === 2) {
        typeOfSearchColumnName = 'Internal Id';
        typeOfSearchColumn = 'internalId';
    }
    else {
        typeOfSearchColumnName = 'Columns Filter';
        typeOfSearchColumn = 'columnsFilter';
    };


    const formatValidatedData = (data) => {
        const result = data.map((row) => {
            const cells = row;
            if (typeOfSearchColumn === 'partName') {
                return {
                    partName: cells.name,
                    exist: cells.exist === 1 ? 'Yes' : 'No'
                };
            }
            else {
                return {
                    internalId: cells.name,
                    exist: cells.exist === 1 ? 'Yes' : 'No'
                };
            }

        });
        return result;

    }

    const saveMasterGridFilter = async () => {
        const response = typeOfSearchId === 3 ? postMasterGridColumnFilter(filterRows) : postMasterGridFilter(dataValidated, typeOfSearchId);
        const refreshTime = (new Date()).getTime();
        const filterUrl = (typeOfSearchId === 3 ? '/api/Items/itemColumnsMassiveFilter' : '/api/Items/itemMassiveFilter') + '?refresh=' + refreshTime;
        setFilterName(response[0]?.filterName);
        onApplyFilter(response, filterUrl);
    }

    const reportValidateMasterGridFilter = async (data) => {
        const response = await postValidateFilter(data, typeOfSearchId);
        const formatData = formatValidatedData(response);
        setDataValidated(formatData);
    }

    const dataHasErrors = dataValidated?.some((item) => item.exist === 'No');

    const onStepContinue = (e) => {
        if (step === 1 && typeOfSearchId !== 3) {
            reportValidateMasterGridFilter(data);
            setStep(step + 1);
        }
        else if (step === 1 && typeOfSearchId === 3) {
            // For Columns Filter, move directly to review step
            setStep(step + 1);
        }
        else if (step === 2 && typeOfSearchId !== 3) {
            reportValidateMasterGridFilter(dataValidated);
            setShouldShowError(true);
        }
        else {
            setStep(step + 1);
        }
    };

    const onStepFinalize = (e) => {
        saveMasterGridFilter();
    };

    const onStepBack = e => {
        setStep(step - 1);
    };

    const onClearData = e => {
        setData([]);
    };


    const renderStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <div>
                        <FilterTypeSelection typeOfSearchId={typeOfSearchId} setTypeOfSearchId={setTypeOfSearchId} />
                    </div>
                );
            case 1:
                if (typeOfSearchId === 3) {
                    return (
                        <div>
                            <ColumnsFilter 
                                typeOfSearchId={typeOfSearchId} 
                                setTypeOfSearchId={setTypeOfSearchId} 
                                filterRows={filterRows} 
                                setFilterRows={setFilterRows} 
                                setFilterName={setFilterName} 
                                setSelectedSearch={setSelectedSearch}
                                onNext={() => onStepContinue()}
                                onBack={() => onStepBack()}
                                currentStep={step + 1}
                                totalSteps={3}
                                stepTitle="Configure Filters"
                            />
                        </div>
                    );
                }
                else {
                    return (
                        <div>
                            <CopyPasteGrid 
                                data={data} 
                                setData={setData} 
                                typeOfSearchColumnName={typeOfSearchColumnName} 
                                typeOfSearchColumn={typeOfSearchColumn} 
                                shouldShowError={shouldShowError} 
                                setFilterName={setFilterName} 
                                setSelectedSearch={setSelectedSearch}
                                onNext={() => onStepContinue()}
                                onBack={() => onStepBack()}
                                onClearData={() => onClearData()}
                                currentStep={step + 1}
                                totalSteps={3}
                                stepTitle="Select Values"
                            />
                        </div>
                    );
                };
            case 2:
                if (typeOfSearchId === 3) {
                    return (
                        <div>
                            <ColumnsFilterReview 
                                filterRows={filterRows}  
                                filterName={filterName} 
                                setFilterName={setFilterName} 
                                selectedSearch={selectedSearch}
                                onBack={() => onStepBack()}
                                onApply={() => onStepFinalize()}
                                currentStep={3}
                                totalSteps={3}
                                stepTitle="Review & Apply Filter"
                            />
                        </div>
                    );
                }
                else {
                    return (
                        <div>
                            <FilterValidationGrid data={data} dataValidated={dataValidated} setDataValidated={setDataValidated} typeOfSearchColumnName={typeOfSearchColumnName} typeOfSearchColumn={typeOfSearchColumn} typeOfSearchId={typeOfSearchId} filterName={filterName} selectedSearch={selectedSearch} setFilterName={setFilterName} shouldShowError={shouldShowError} setShouldShowError={setShouldShowError} />
                        </div>
                    );
                }

            default:
                return 0;
        }
    }

    return (
        <>
            <Stepper
                steps={[{ label: 'Select Type Of Search' }, { label: 'Select Values' }, { label: 'Final Results' }]}
                activeStep={step}
                connectorStateColors={true}
                styleConfig={{
                    activeBgColor: '#007bff',
                    activeTextColor: '#fff',
                    inactiveBgColor: '#f8f9fa',
                    inactiveTextColor: '#6c757d',
                    completedBgColor: '#28a745',
                    completedTextColor: '#fff',
                    size: '2.5rem',
                    circleFontSize: '0.875rem',
                    labelFontSize: '0.75rem',
                    borderRadius: '8px',
                    fontWeight: '600'
                }}
                connectorStyleConfig={{
                    activeColor: '#007bff',
                    completedColor: '#28a745',
                    disabledColor: '#dee2e6',
                    size: '3px',
                    style: 'solid'
                }}
                className={'stepper'}
                stepClassName={'stepper__step'}
            />
            {renderStepContent(step)}
            {/* Bottom Navigation - Only show when components don't have integrated navigation */}
            {(step === 0 || (step === 2 && typeOfSearchId !== 3)) && (
                <div style={{ 
                    display: "flex", 
                    justifyContent: step === 0 ? "flex-end" : "space-between", 
                    marginTop: "1.5rem", 
                    padding: "1rem 0",
                    borderTop: "1px solid #dee2e6",
                    minHeight: "60px"
                }}>
                    {/* Back button for step 2 (Final Results) */}
                    {step === 2 && typeOfSearchId !== 3 && (
                        <div style={{ 
                            display: "flex", 
                            alignItems: "center"
                        }}>
                            <Button
                                text="Back"
                                onClick={() => onStepBack()}
                                type="normal"
                                stylingMode="outlined"
                            />
                        </div>
                    )}
                    
                    {/* Next button for step 0 */}
                    {step === 0 && (
                        <div style={{ 
                            display: "flex", 
                            alignItems: "center"
                        }}>
                            <Button
                                text="Next"
                                onClick={() => onStepContinue()}
                                type="default"
                                stylingMode="contained"
                            />
                        </div>
                    )}
                    
                    {/* Action buttons for step 2 */}
                    {step === 2 && typeOfSearchId !== 3 && (
                        <div style={{ 
                            display: "flex", 
                            gap: "0.5rem",
                            alignItems: "center"
                        }}>
                            <Button
                                text="Revalidate Filter"
                                onClick={() => onStepContinue()}
                                type="default"
                                stylingMode="outlined"
                            />
                            <Button
                                text="Apply Filter"
                                onClick={() => onStepFinalize()}
                                disabled={filterName === null || dataHasErrors}
                                type="success"
                                stylingMode="contained"
                            />
                        </div>
                    )}
                </div>
            )}

        </>
    );
};
